import { useContext, useEffect, useState } from 'react';
import { Box, Button, useMediaQuery } from '@mui/material';
import { UserContext } from 'components/App';
import { requestAPI } from 'API/requestApi';
import { Navigate, useNavigate } from 'react-router';
import Loading from 'components/Loading';
import AOCError from 'API/AOCError';
import { showHideInfoDialog } from 'state/tasks/tasksSlice';
import { useDispatch } from 'react-redux';
import { isUserInternal } from 'hooks/useIsInternalUser';

type EphemeralCodeType = {
	allowExternal: boolean;
	displayUserHTML: string;
	ttlInSecondsOnceApproved: number;
	redirectPath: string | null;
	targetUserId: string | null;
	status: 'pending' | 'approved';
};

const getCode = (code: string): Promise<EphemeralCodeType | null> => {
	return requestAPI(`/auth/ephemeral-code/${code}`, 'GET', { 'Content-type': 'application/json' }).catch((err) => {
		if (err instanceof AOCError) {
			if (err.errorCode === 'INVALID_AUTH_CODE') {
				return null;
			}
		}
		throw err;
	});
};

const approveCode = (code: string): Promise<boolean> => {
	return requestAPI(`/auth/ephemeral-code/${code}/approve`, 'POST', { 'Content-type': 'application/json' }).then(() => true).catch(() => {
		return false;
	});
};

const ExternalPermissions = () => {
	const navigate = useNavigate();
	const params = new URLSearchParams(window.location.search);
	const user = useContext(UserContext);
	const dispatch = useDispatch();
	const [loading, setLoading] = useState<boolean>(false);
	const [codeInfoResponse, setCodeInfoResponse] = useState<EphemeralCodeType | null | undefined>(undefined);
	const isPortrait = useMediaQuery('(orientation: portrait)');

	const infoErrorDialog = showHideInfoDialog({ title: 'Stop!', message: 'Koda ni veljavna ali pa je že potekla.' });

	const paramsCode = params.get('code') as string;

	useEffect(() => {
		(async () => {
			if (!user || !paramsCode) return;
			const codeInfo = await getCode(paramsCode);

			setCodeInfoResponse(codeInfo);
		})();
	}, [setCodeInfoResponse, user, paramsCode]);

	if (!user || !paramsCode) {
		return <Navigate replace to='/' />;
	}

	const isExternal = !isUserInternal(user);

	if (codeInfoResponse !== undefined && (codeInfoResponse === null || codeInfoResponse.status !== 'pending')) {
		dispatch(infoErrorDialog);
		navigate('/events');
	}

	if (codeInfoResponse && isExternal && !codeInfoResponse.allowExternal) {
		dispatch(showHideInfoDialog({ title: 'Stop!', message: 'Koda je veljavna samo za račune ustvarjene pod domeno @inova.si' }));
		navigate('/events');
	}

	if (codeInfoResponse && codeInfoResponse.targetUserId !== null && codeInfoResponse.targetUserId !== user.uid) {
		dispatch(showHideInfoDialog({ title: 'Stop!', message: 'Ta koda ni zate - preveri, če si prijavljen z ustreznim računom.' }));
		navigate('/events');
	}

	return <div style={{ margin: '16px 16px 16px 16px' }}>
		{!codeInfoResponse && <div style={{ marginLeft: '0px', paddingTop: 25 }}><Loading /></div>}
		{codeInfoResponse && (<div><p dangerouslySetInnerHTML={{ __html: codeInfoResponse.displayUserHTML }}></p> <p></p>
			<p>
				<Box sx={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: isPortrait ? 'space-between' : 'left',
					width: '100%',
					px: 2,
				}}>
					<Button size='large' sx={{ fontSize: '1.2rem' }} disabled={loading} onClick={async () => {
						setLoading(true);
						const ok = await approveCode(paramsCode);

						setLoading(false);
						if (!ok) {
							dispatch(infoErrorDialog);
							navigate('/events');
						} else {
							dispatch(showHideInfoDialog({ title: 'Odobreno ✅', message: 'Vaša zahteva je bila uspešno potrjena.' }));
							const redirectPath = codeInfoResponse.redirectPath;

							if (redirectPath) {
								setTimeout(() => navigate(redirectPath), 0);
							} else {
								navigate('/events');
							}
						}
					}}> POTRDI </Button>

					{<Button size='large' sx={{ fontSize: '1.2rem', marginLeft: isPortrait ? undefined : '8rem' }} disabled={loading} color='error' onClick={() => {
						dispatch(showHideInfoDialog({ title: 'Zavrnjeno ❌', message: 'Če je šlo za pomoto, poskusite znova.' }));
						navigate('/events');
					}}> ZAVRNI </Button>}
				</Box>
			</p></div>)}

	</div >;
};

export default ExternalPermissions;
